import { useState } from 'react';
import type { MailClient } from '../api/client';
import type { EmailBodyPart } from '../api/types';
import { useT } from '../i18n';
import { fileSize } from '../mail/format';
import { extensionOf, lookOf, thumbnailable } from '../mail/filetype';

/*
    One attachment, as a card.

    It used to be a row: a small extension badge, the name, the size. Six attachments made six
    identical grey lines, and finding "the photo" among them meant reading six file names — which
    is the slowest way a person can be asked to recognise a picture they have already seen.

    So an image shows the image. Everything else shows what kind of thing it is, in a colour that
    is the same for every spreadsheet and a different one for every PDF, so the shape of the list
    can be read before any of the words in it are.

    The thumbnail is the file itself, at the size the browser draws it — there is no server-side
    resizer, so a large picture would mean downloading a large picture to draw a small square.
    `thumbnailable` refuses past a couple of megabytes and the card falls back to its badge, which
    is the honest trade: a reader on a phone should not pay for a preview they did not ask for.
*/
export default function AttachmentCard({
    client,
    emailId,
    part,
    onOpen,
    openable,
}: {
    client: MailClient | undefined;
    emailId: string;
    part: EmailBodyPart;
    onOpen: () => void;
    openable: boolean;
}) {
    const { t, lang } = useT();
    const [thumbBroken, setThumbBroken] = useState(false);
    const look = lookOf(part.type, part.name);

    /*
        The name without its extension.

        The badge above already says DOCX; repeating it costs the four characters that push a long
        Turkish file name onto a third line — and when it wraps it breaks mid-suffix, so the card
        reads "carpim_tablosu_testler.d / ocx". The whole name is still on the tooltip and still
        what gets saved.
    */
    const full = part.name ?? t('reader.unnamed');
    const ext = extensionOf(part.name);
    const shown = ext ? full.slice(0, full.length - ext.length - 1) : full;

    // Pointed at directly rather than fetched: the bytes never pass through the page, the browser
    // caches it, and the response keeps the headers the server put on it.
    const thumb =
        client && part.partId && !thumbBroken && thumbnailable(part.type, part.size)
            ? client.attachmentUrl(emailId, part.partId, true)
            : null;

    // Straight to the server, like the card's own download: the browser streams it to disk.
    const download = client && part.partId ? client.attachmentUrl(emailId, part.partId) : null;

    /*
        Two targets, not one. The card opens what can be opened, so a previewable file could
        only be saved by opening it first and finding "Kaydet" in the overlay (asked for,
        2026-10-12). Saving is now said in words under every card, the same whether or not the
        file can be previewed.
    */
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: 168,
                overflow: 'hidden',
                background: 'var(--surface2)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--r-btn)',
            }}
        >
            <button
                onClick={onOpen}
                title={`${full} — ${openable ? t('preview.open') : t('attachment.download')}`}
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                    padding: 0,
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    borderRadius: 0,
                    color: 'var(--text)',
                }}
            >
                {/* The picture, or the kind of thing it is. Same height either way, so a row of mixed
                    attachments lines up instead of stepping. */}
                <span
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: 92,
                        background: thumb ? 'var(--chip-bg)' : `${look.tint}1f`,
                        borderBottom: '1px solid var(--border)',
                        overflow: 'hidden',
                    }}
                >
                    {thumb ? (
                        <img
                            src={thumb}
                            alt=""
                            onError={() => setThumbBroken(true)}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    ) : (
                        <span
                            style={{
                                fontFamily: 'var(--font-mono)',
                                fontSize: 'var(--fs-sm)',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                color: look.tint,
                            }}
                        >
                            {look.label}
                        </span>
                    )}
                </span>

                <span style={{ display: 'grid', gap: 2, padding: 'var(--sp-4) var(--sp-5)', minWidth: 0 }}>
                    <span
                        style={{
                            fontSize: 'var(--fs-xs)',
                            // Two lines then stop: a Turkish file name is long and a card that grows
                            // to fit one pushes every other card out of line.
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                        }}
                    >
                        {shown}
                    </span>
                    <span
                        style={{
                            fontFamily: 'var(--font-mono)',
                            fontSize: 'var(--fs-2xs)',
                            color: 'var(--text-mute)',
                        }}
                    >
                        {fileSize(part.size, lang === 'tr' ? 'tr-TR' : 'en-GB')}
                    </span>
                </span>
            </button>
            {download ? (
                <a
                    href={download}
                    // No value: the name comes from the server, which has already cleaned it
                    // (attachment.go), rather than from the sender's raw one.
                    download
                    rel="noopener"
                    data-testid="attachment-download"
                    title={`${full} — ${t('attachment.download')}`}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 'var(--sp-3)',
                        padding: 'var(--sp-3) var(--sp-5)',
                        borderTop: '1px solid var(--border)',
                        background: 'var(--accent-bg)',
                        color: 'var(--accent-strong)',
                        fontSize: 'var(--fs-xs)',
                        fontWeight: 600,
                        textDecoration: 'none',
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
                    </svg>
                    {t('attachment.download')}
                </a>
            ) : null}
        </div>
    );
}
